import React, { useState, useEffect } from 'react';
import { 
  Users, UserCheck, Search, Filter, Plus, ArrowRight, DollarSign, 
  Calendar, Clock, CheckCircle2, AlertCircle, Phone, MessageCircle, 
  FileText, Copy, ExternalLink, ShieldCheck, Sparkles, Send, Eye,
  CreditCard, ChevronRight, X, ArrowUpRight, TrendingUp, History,
  Receipt, Settings2, QrCode, Tag, Check, ArrowLeft, MoreHorizontal,
  Mail, MapPin, Edit3, Trash2, Download, CheckCircle, RefreshCw
} from 'lucide-react';
import { CustomerProfile, CustomerInvoice, ClientPaymentConfig, ClientEntity } from '../types';
import { db } from '../services/db';
import { toast } from 'sonner';

interface ClientDossierManagerProps {
  client: ClientEntity;
  primaryColor?: string;
}

export const ClientDossierManager: React.FC<ClientDossierManagerProps> = ({ client, primaryColor = '#059669' }) => {
  const [profiles, setProfiles] = useState<CustomerProfile[]>([]);
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
  const [paymentConfig, setPaymentConfig] = useState<ClientPaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);

  // Main List Tabs
  const [activeTab, setActiveTab] = useState<'todos' | 'clientes' | 'leads' | 'cobrancas' | 'config_pagamento'>('todos');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selected Profile for FULL-PAGE DOSSIER VIEW (Not a popup!)
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [dossierTab, setDossierTab] = useState<'visao_geral' | 'faturas' | 'servicos' | 'linha_do_tempo' | 'notas'>('visao_geral');

  // New Invoice Modal (Simple modal for action)
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [invoiceTargetProfile, setInvoiceTargetProfile] = useState<CustomerProfile | null>(null);
  const [invoiceTitle, setInvoiceTitle] = useState('Consulta & Acompanhamento');
  const [invoiceAmount, setInvoiceAmount] = useState('350.00');
  const [invoiceDueDate, setInvoiceDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return d.toISOString().split('T')[0];
  });
  const [invoiceDescription, setInvoiceDescription] = useState('Atendimento especializado com suporte e orientações.');
  const [isSubmittingInvoice, setIsSubmittingInvoice] = useState(false);

  // New Lead/Client Modal
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newCustomerType, setNewCustomerType] = useState<'lead' | 'cliente'>('lead');
  const [newCustomerSource, setNewCustomerSource] = useState('Site Oficial');
  const [newCustomerNotes, setNewCustomerNotes] = useState('');

  // Payment Config Form
  const [pixKeyType, setPixKeyType] = useState<'cnpj' | 'cpf' | 'email' | 'phone' | 'random'>('cnpj');
  const [pixKey, setPixKey] = useState('');
  const [pixReceiverName, setPixReceiverName] = useState('');
  const [pixCity, setPixCity] = useState('');
  const [cardPaymentLink, setCardPaymentLink] = useState('');
  const [cardInstructions, setCardInstructions] = useState('');
  const [gatewayProvider, setGatewayProvider] = useState<'mercadopago' | 'asaas' | 'stripe' | 'none'>('mercadopago');
  const [gatewayToken, setGatewayToken] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => {
    loadData();
  }, [client.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cProfiles, cInvoices, pConfig] = await Promise.all([
        db.getCustomerProfiles(client.id),
        db.getCustomerInvoices(client.id),
        db.getClientPaymentConfig(client.id)
      ]);
      setProfiles(cProfiles);
      setInvoices(cInvoices);
      setPaymentConfig(pConfig);

      if (pConfig) {
        setPixKeyType(pConfig.pixKeyType || 'cnpj');
        setPixKey(pConfig.pixKey || '');
        setPixReceiverName(pConfig.pixReceiverName || client.data?.name || '');
        setPixCity(pConfig.pixCity || 'São Paulo');
        setCardPaymentLink(pConfig.cardPaymentLink || '');
        setCardInstructions(pConfig.cardLinkInstructions || '');
        setGatewayProvider(pConfig.gatewayProvider || 'mercadopago');
        setGatewayToken(pConfig.gatewayToken || '');
      }
    } catch (e) {
      toast.error('Erro ao carregar dados da carteira.');
    } finally {
      setLoading(false);
    }
  };

  const selectedProfile = profiles.find(p => p.id === selectedProfileId) || null;

  // Global KPIs
  const totalRevenue = profiles.reduce((acc, p) => acc + (p.totalRevenue || 0), 0);
  const totalPending = profiles.reduce((acc, p) => acc + (p.totalPending || 0), 0);
  const totalClientsCount = profiles.filter(p => p.type === 'cliente').length;
  const totalLeadsCount = profiles.filter(p => p.type === 'lead').length;

  const filteredProfiles = profiles.filter(p => {
    if (activeTab === 'clientes' && p.type !== 'cliente') return false;
    if (activeTab === 'leads' && p.type !== 'lead') return false;
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase();
    return (
      p.name.toLowerCase().includes(term) ||
      p.phone.includes(term) ||
      (p.email && p.email.toLowerCase().includes(term))
    );
  });

  const handleOpenNewInvoice = (profile: CustomerProfile) => {
    setInvoiceTargetProfile(profile);
    setIsInvoiceModalOpen(true);
  };

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoiceTargetProfile) return;

    const parsedAmount = parseFloat(invoiceAmount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error('Por favor, informe um valor válido.');
      return;
    }

    setIsSubmittingInvoice(true);
    try {
      const invNumber = `FAT-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
      const cleanPhone = invoiceTargetProfile.phone.replace(/[^0-9]/g, '');

      const pixPayload = `00020126580014br.gov.bcb.pix0136123e4567-e89b-12d3-a456-4266141740005204000053039865405${parsedAmount.toFixed(2)}5802BR5913${(paymentConfig?.pixReceiverName || client.data.name).substring(0, 25)}6009Sao Paulo62070503***6304E2CA`;

      const created = await db.createCustomerInvoice({
        invoiceNumber: invNumber,
        clientId: client.id,
        clientSlug: client.slug,
        customerPhone: invoiceTargetProfile.phone,
        customerName: invoiceTargetProfile.name,
        customerEmail: invoiceTargetProfile.email,
        title: invoiceTitle,
        description: invoiceDescription,
        amount: parsedAmount,
        dueDate: invoiceDueDate,
        status: 'pending',
        paymentMethodType: 'pix',
        pixCode: pixPayload,
        cardPaymentUrl: paymentConfig?.cardPaymentLink || undefined
      });

      toast.success(`Cobrança de R$ ${parsedAmount.toFixed(2)} gerada com sucesso!`);
      setIsInvoiceModalOpen(false);

      const publicInvoiceLink = `${window.location.origin}/#/fatura/${created.id}`;
      const waMsg = encodeURIComponent(
        `Olá ${invoiceTargetProfile.name}! 👋\n\nAqui está a fatura referente a *${invoiceTitle}* no valor de *R$ ${parsedAmount.toFixed(2)}* com vencimento em *${invoiceDueDate}*.\n\n🔗 *Pague com Pix ou Cartão aqui:* ${publicInvoiceLink}\n\nSe preferir, acesse sua Área do Cliente com seu número de WhatsApp: ${window.location.origin}/#/atendimento/${client.slug}`
      );
      const waUrl = `https://wa.me/55${cleanPhone}?text=${waMsg}`;
      window.open(waUrl, '_blank');

      loadData();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao emitir cobrança.');
    } finally {
      setIsSubmittingInvoice(false);
    }
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustomerName.trim() || !newCustomerPhone.trim()) {
      toast.error('Preencha o nome e telefone do cliente.');
      return;
    }

    try {
      const newProf: CustomerProfile = {
        id: `cust_${Date.now()}`,
        clientId: client.id,
        name: newCustomerName.trim(),
        phone: newCustomerPhone.trim(),
        email: newCustomerEmail.trim() || undefined,
        type: newCustomerType,
        status: newCustomerType === 'cliente' ? 'ativo' : 'novo',
        createdAt: new Date().toISOString(),
        totalRevenue: 0,
        totalPending: 0,
        assignedTo: client.data?.name || 'Gestor',
        source: newCustomerSource,
        notes: newCustomerNotes,
        purchasedServices: [],
        timeline: [
          {
            id: `ev_${Date.now()}`,
            type: 'lead_captured',
            title: newCustomerType === 'cliente' ? 'Cliente Cadastrado' : 'Novo Lead Adicionado',
            description: `Registro inserido no Cockpit de Gestão.`,
            timestamp: new Date().toISOString()
          }
        ]
      };

      await db.saveCustomerProfile(newProf);
      toast.success(`${newCustomerType === 'cliente' ? 'Cliente' : 'Lead'} cadastrado com sucesso!`);
      setIsAddCustomerModalOpen(false);
      setNewCustomerName('');
      setNewCustomerPhone('');
      setNewCustomerEmail('');
      setNewCustomerNotes('');
      loadData();
    } catch (e) {
      toast.error('Erro ao cadastrar.');
    }
  };

  const handleSavePaymentConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingConfig(true);
    try {
      const config: ClientPaymentConfig = {
        clientId: client.id,
        pixKeyType,
        pixKey: pixKey.trim(),
        pixReceiverName: pixReceiverName.trim() || client.data?.name,
        pixCity: pixCity.trim() || 'São Paulo',
        cardPaymentLink: cardPaymentLink.trim(),
        cardLinkInstructions: cardInstructions.trim(),
        gatewayProvider,
        gatewayToken: gatewayToken.trim(),
        gatewayActive: gatewayProvider !== 'none' && !!gatewayToken.trim(),
        allowPixDirect: true,
        allowCardLink: !!cardPaymentLink.trim()
      };

      await db.saveClientPaymentConfig(config);
      setPaymentConfig(config);
      toast.success('Configurações de recebimento salvas com sucesso!');
    } catch (e) {
      toast.error('Falha ao salvar configurações.');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleMarkInvoiceAsPaid = async (invId: string) => {
    try {
      await db.payCustomerInvoice(invId, 'pix');
      toast.success('Fatura marcada como PAGA com sucesso!');
      loadData();
    } catch (e) {
      toast.error('Erro ao atualizar fatura.');
    }
  };

  const handleSendPortalAccess = (p: CustomerProfile) => {
    const cleanPhone = p.phone.replace(/[^0-9]/g, '');
    const portalUrl = `${window.location.origin}/#/atendimento/${client.slug}`;
    const msg = encodeURIComponent(
      `Olá ${p.name}! 👋\n\nSua Área Exclusiva na *${client.data?.name}* já está liberada. Acesse para acompanhar agendamentos, faturas Pix e documentos.\n\n🔗 *Acesse aqui:* ${portalUrl}\n📱 *Seu acesso:* ${p.phone}`
    );
    window.open(`https://wa.me/55${cleanPhone}?text=${msg}`, '_blank');
    toast.success('Link do portal enviado no WhatsApp!');
  };

  // ==========================================
  // VIEW 1: FULL-PAGE CUSTOMER DOSSIER 360°
  // ==========================================
  if (selectedProfile) {
    const cleanPhone = selectedProfile.phone.replace(/[^0-9]/g, '');
    const customerInvoices = invoices.filter(inv => inv.customerPhone === selectedProfile.phone);

    return (
      <div className="w-full min-h-full flex flex-col bg-white animate-in fade-in duration-200">
        
        {/* Top Sticky Navigation Bar */}
        <div className="border-b border-slate-200 bg-white sticky top-0 z-30 px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedProfileId(null)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              <ArrowLeft size={14} /> Voltar para Carteira
            </button>
            <span className="text-slate-300">/</span>
            <span className="text-xs font-semibold text-slate-500">Dossiê 360° do Cliente</span>
            <span className="text-slate-300">/</span>
            <span className="text-xs font-bold text-slate-900">{selectedProfile.name}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSendPortalAccess(selectedProfile)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              <Send size={13} className="text-slate-500" /> Enviar Portal no WhatsApp
            </button>
            <button
              onClick={() => handleOpenNewInvoice(selectedProfile)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold text-white shadow-sm hover:opacity-90 transition-all"
              style={{ backgroundColor: primaryColor }}
            >
              <DollarSign size={14} /> Emitir Cobrança Pix/Cartão
            </button>
          </div>
        </div>

        {/* Profile Hero Header */}
        <div className="border-b border-slate-200 bg-slate-50/50 px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start md:items-center gap-5">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center font-black text-2xl text-white shadow-sm shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                {selectedProfile.name.charAt(0).toUpperCase()}
              </div>

              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-black text-slate-900 tracking-tight">{selectedProfile.name}</h1>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                    selectedProfile.type === 'cliente' 
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                      : 'bg-amber-100 text-amber-800 border border-amber-200'
                  }`}>
                    {selectedProfile.type === 'cliente' ? 'Cliente Ativo' : 'Lead em Aberto'}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-y-1 gap-x-5 text-xs text-slate-600 mt-2">
                  <span className="flex items-center gap-1.5 font-medium">
                    <Phone size={13} className="text-slate-400" /> {selectedProfile.phone}
                  </span>
                  {selectedProfile.email && (
                    <span className="flex items-center gap-1.5 font-medium">
                      <Mail size={13} className="text-slate-400" /> {selectedProfile.email}
                    </span>
                  )}
                  <span className="text-slate-500">
                    Origem: <b className="text-slate-700">{selectedProfile.source || 'Site Oficial'}</b>
                  </span>
                  <span className="text-slate-500">
                    Cadastrado em: <b className="text-slate-700">{new Date(selectedProfile.createdAt).toLocaleDateString('pt-BR')}</b>
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Metrics Bar */}
            <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-3 shadow-xs">
              <div className="px-3 border-r border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">LTV Acumulado</span>
                <span className="text-lg font-black text-slate-900">
                  R$ {selectedProfile.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="px-3 border-r border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Pendente</span>
                <span className={`text-lg font-black ${selectedProfile.totalPending > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                  R$ {selectedProfile.totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="px-3">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Faturas</span>
                <span className="text-lg font-black text-slate-900">{customerInvoices.length}</span>
              </div>
            </div>
          </div>

          {/* TOP HORIZONTAL SUBMENU TABS */}
          <div className="flex items-center gap-8 mt-6 -mb-6 border-t border-slate-200/80 pt-1">
            {[
              { id: 'visao_geral', label: 'Visão Geral & Dados' },
              { id: 'faturas', label: 'Faturas & Cobranças', count: customerInvoices.length },
              { id: 'servicos', label: 'Serviços & Contratos', count: selectedProfile.purchasedServices?.length || 0 },
              { id: 'linha_do_tempo', label: 'Linha do Tempo & Histórico', count: selectedProfile.timeline?.length || 0 },
              { id: 'notas', label: 'Notas & Observações' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setDossierTab(tab.id as any)}
                className={`py-3.5 text-xs font-bold border-b-2 transition-all flex items-center gap-2 ${
                  dossierTab === tab.id
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                }`}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    dossierTab === tab.id ? 'bg-slate-900 text-white' : 'bg-slate-200/70 text-slate-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Dossier Content Body */}
        <div className="flex-1 p-8 bg-slate-50/30">
          
          {/* TAB 1: VISÃO GERAL */}
          {dossierTab === 'visao_geral' && (
            <div className="max-w-6xl space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* Financial Summary */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                      <TrendingUp size={15} className="text-emerald-600" /> Saúde Financeira
                    </h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Valor Total Faturado:</span>
                      <span className="font-bold text-slate-900">R$ {selectedProfile.totalRevenue.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Valores em Aberto:</span>
                      <span className="font-bold text-amber-600">R$ {selectedProfile.totalPending.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500">Total de Faturas:</span>
                      <span className="font-bold text-slate-900">{customerInvoices.length} emitidas</span>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      onClick={() => handleOpenNewInvoice(selectedProfile)}
                      className="w-full py-2.5 px-3 rounded-lg text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus size={14} /> Nova Cobrança Instantânea
                    </button>
                  </div>
                </div>

                {/* Contact & Registration Data */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs space-y-4 md:col-span-2">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                      <UserCheck size={15} className="text-slate-700" /> Dados Cadastrais & Acessos
                    </h3>
                    <a
                      href={`https://wa.me/55${cleanPhone}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600 hover:text-emerald-700"
                    >
                      <MessageCircle size={14} /> Iniciar Conversa no WhatsApp
                    </a>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-slate-400 block font-medium">Nome Completo:</span>
                      <span className="font-bold text-slate-900">{selectedProfile.name}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-medium">Telefone / WhatsApp:</span>
                      <span className="font-bold text-slate-900">{selectedProfile.phone}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-medium">E-mail:</span>
                      <span className="font-bold text-slate-900">{selectedProfile.email || 'Não informado'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-medium">Origem do Lead:</span>
                      <span className="font-bold text-slate-900">{selectedProfile.source || 'Site Oficial'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-medium">Responsável Interno:</span>
                      <span className="font-bold text-slate-900">{selectedProfile.assignedTo || client.data.name}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-medium">Link do Portal do Cliente:</span>
                      <span className="font-mono text-slate-600 truncate block">
                        {window.location.origin}/#/atendimento/{client.slug}
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              {/* Quick Timeline preview */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs">
                <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                  <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <History size={15} className="text-slate-700" /> Últimas Atividades
                  </h3>
                  <button 
                    onClick={() => setDossierTab('linha_do_tempo')} 
                    className="text-xs font-bold text-slate-600 hover:text-slate-900"
                  >
                    Ver linha do tempo completa →
                  </button>
                </div>

                <div className="mt-4 space-y-3">
                  {selectedProfile.timeline?.slice(0, 3).map(event => (
                    <div key={event.id} className="flex items-start gap-3 text-xs">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                      <div className="flex-1">
                        <span className="font-bold text-slate-900">{event.title}</span>
                        <p className="text-slate-500 mt-0.5">{event.description}</p>
                      </div>
                      <span className="text-[11px] text-slate-400 font-medium">
                        {new Date(event.timestamp).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: FATURAS & COBRANÇAS */}
          {dossierTab === 'faturas' && (
            <div className="max-w-6xl space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Histórico de Cobranças & Faturas</h3>
                  <p className="text-xs text-slate-500">Todas as faturas geradas para este contato com status em tempo real.</p>
                </div>

                <button
                  onClick={() => handleOpenNewInvoice(selectedProfile)}
                  className="px-4 py-2 rounded-lg text-xs font-bold text-white shadow-sm hover:opacity-90 transition-all flex items-center gap-2"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Plus size={14} /> Emitir Fatura Pix / Cartão
                </button>
              </div>

              {customerInvoices.length > 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                      <tr>
                        <th className="py-3 px-4">Número / Título</th>
                        <th className="py-3 px-4">Valor</th>
                        <th className="py-3 px-4">Vencimento</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-right">Ações Rápidas</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {customerInvoices.map(inv => {
                        const isPaid = inv.status === 'paid';
                        const publicLink = `${window.location.origin}/#/fatura/${inv.id}`;

                        return (
                          <tr key={inv.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-3.5 px-4">
                              <div className="font-bold text-slate-900">{inv.title}</div>
                              <div className="text-[11px] font-mono text-slate-400">{inv.invoiceNumber}</div>
                            </td>
                            <td className="py-3.5 px-4 font-black text-slate-900">
                              R$ {inv.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-3.5 px-4 text-slate-600">
                              {new Date(inv.dueDate).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="py-3.5 px-4">
                              <span className={`px-2.5 py-1 rounded-full font-bold uppercase text-[10px] ${
                                isPaid 
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                                  : 'bg-amber-100 text-amber-800 border border-amber-200'
                              }`}>
                                {isPaid ? 'Pago' : 'Pendente'}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {!isPaid && (
                                  <button
                                    onClick={() => handleMarkInvoiceAsPaid(inv.id)}
                                    className="px-2.5 py-1.5 rounded-md font-bold text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors flex items-center gap-1"
                                  >
                                    <CheckCircle size={12} /> Confirmar Pgto
                                  </button>
                                )}
                                <a
                                  href={publicLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="px-2.5 py-1.5 rounded-md font-bold text-[11px] text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors flex items-center gap-1"
                                >
                                  <ExternalLink size={12} /> Ver Fatura
                                </a>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-white p-8 rounded-xl border border-slate-200 text-center">
                  <Receipt size={32} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm font-bold text-slate-700">Nenhuma fatura emitida para este contato ainda.</p>
                  <p className="text-xs text-slate-400 mt-1">Gere cobranças instantâneas com chave Pix e link de cartão.</p>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: SERVIÇOS & CONTRATOS */}
          {dossierTab === 'servicos' && (
            <div className="max-w-6xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Serviços, Planos & Contratos</h3>
                  <p className="text-xs text-slate-500">Histórico de pacotes e consultas adquiridas pelo cliente.</p>
                </div>
              </div>

              {selectedProfile.purchasedServices && selectedProfile.purchasedServices.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedProfile.purchasedServices.map(srv => (
                    <div key={srv.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between">
                      <div>
                        <div className="font-bold text-slate-900 text-sm">{srv.title}</div>
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                          <span>{srv.category || 'Atendimento'}</span>
                          <span>•</span>
                          <span>{srv.date}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-black text-slate-900 text-base">R$ {srv.amount.toFixed(2)}</span>
                        <div className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full mt-1">
                          CONCLUÍDO
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white p-8 rounded-xl border border-slate-200 text-center">
                  <Receipt size={32} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm font-bold text-slate-700">Nenhum serviço registrado.</p>
                  <p className="text-xs text-slate-400 mt-1">Os serviços pagos nas faturas são sincronizados automaticamente aqui.</p>
                </div>
              )}
            </div>
          )}

          {/* TAB 4: LINHA DO TEMPO */}
          {dossierTab === 'linha_do_tempo' && (
            <div className="max-w-4xl bg-white p-6 rounded-xl border border-slate-200 shadow-xs space-y-6">
              <div>
                <h3 className="text-base font-bold text-slate-900">Linha do Tempo Completa</h3>
                <p className="text-xs text-slate-500">Registro histórico de todas as interações, acessos e transações.</p>
              </div>

              <div className="space-y-4 relative before:absolute before:left-3.5 before:top-3 before:bottom-3 before:w-0.5 before:bg-slate-200">
                {selectedProfile.timeline?.map(event => (
                  <div key={event.id} className="relative pl-9">
                    <div className="absolute left-2 top-1.5 -translate-x-1/2 w-3 h-3 rounded-full bg-slate-900 border-2 border-white shadow-xs" />
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-xs text-slate-900">{event.title}</span>
                        <span className="text-[11px] text-slate-400">
                          {new Date(event.timestamp).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{event.description}</p>
                      {event.amount && (
                        <span className="inline-block mt-2 text-xs font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded">
                          R$ {event.amount.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 5: NOTAS & OBSERVAÇÕES */}
          {dossierTab === 'notas' && (
            <div className="max-w-4xl bg-white p-6 rounded-xl border border-slate-200 shadow-xs space-y-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">Anotações Internas do Atendimento</h3>
                <p className="text-xs text-slate-500">Informações confidenciais visíveis apenas para a sua equipe.</p>
              </div>

              <textarea
                value={selectedProfile.notes || ''}
                rows={6}
                onChange={async (e) => {
                  const newNotes = e.target.value;
                  const updated = { ...selectedProfile, notes: newNotes };
                  await db.saveCustomerProfile(updated);
                  setProfiles(prev => prev.map(p => p.id === updated.id ? updated : p));
                }}
                placeholder="Escreva detalhes sobre o perfil do cliente, preferências, histórico de saúde ou observações de vendas..."
                className="w-full p-4 text-xs bg-slate-50 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 leading-relaxed font-medium"
              />
              <p className="text-[11px] text-slate-400">Salvo automaticamente em tempo real.</p>
            </div>
          )}

        </div>

      </div>
    );
  }

  // ==========================================
  // VIEW 2: CARTEIRA DE CONTATOS (ESTILO SAAS / GOHIGHLEVEL)
  // ==========================================
  return (
    <div className="w-full flex flex-col bg-white min-h-full">
      
      {/* Top Header & Fast KPIs Banner */}
      <div className="border-b border-slate-200 px-8 py-6 bg-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700">
                Central de Clientes & Vendas
              </span>
            </div>
            <h1 className="text-2xl font-black text-slate-900 mt-1 tracking-tight">
              Carteira & Dossiês de Clientes
            </h1>
            <p className="text-slate-500 text-xs mt-0.5">
              Gestão fluida de contatos, histórico de atendimentos e emissão de cobranças Pix/Cartão.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => setIsAddCustomerModalOpen(true)}
              className="px-4 py-2 rounded-lg font-bold text-xs text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all flex items-center gap-2"
            >
              <Plus size={15} /> Novo Contato
            </button>
            <button 
              onClick={() => setActiveTab('config_pagamento')}
              className="px-4 py-2 rounded-lg font-bold text-xs text-white shadow-sm hover:opacity-90 transition-all flex items-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              <Settings2 size={15} /> Formas de Receber
            </button>
          </div>
        </div>

        {/* Fast Metrics Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Faturamento (LTV)</span>
            <span className="text-xl font-black text-slate-900">
              R$ {totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </div>

          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Cobranças Pendentes</span>
            <span className="text-xl font-black text-amber-600">
              R$ {totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </div>

          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Clientes Ativos</span>
            <span className="text-xl font-black text-slate-900">{totalClientsCount}</span>
          </div>

          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Leads Novos</span>
            <span className="text-xl font-black text-slate-900">{totalLeadsCount}</span>
          </div>
        </div>

        {/* TOP HORIZONTAL SUBMENUS (GoHighLevel Style) */}
        <div className="flex items-center gap-6 mt-6 -mb-6 border-t border-slate-200/80 pt-1">
          {[
            { id: 'todos', label: 'Todos os Contatos', count: profiles.length },
            { id: 'clientes', label: 'Clientes Ativos', count: totalClientsCount },
            { id: 'leads', label: 'Leads & Oportunidades', count: totalLeadsCount },
            { id: 'cobrancas', label: 'Faturas & Cobranças', count: invoices.length },
            { id: 'config_pagamento', label: 'Configurações de Recebimento (Pix / Cartão)' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-3.5 text-xs font-bold border-b-2 transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  activeTab === tab.id ? 'bg-slate-900 text-white' : 'bg-slate-200/70 text-slate-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="p-8 bg-slate-50/40 flex-1">
        
        {/* TAB: LISTAGEM DE CONTATOS (TODOS, CLIENTES, LEADS) */}
        {(activeTab === 'todos' || activeTab === 'clientes' || activeTab === 'leads') && (
          <div className="space-y-4">
            
            {/* Search & Quick Filter Toolbar */}
            <div className="flex items-center justify-between gap-4 bg-white p-3 rounded-xl border border-slate-200 shadow-xs">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                <input
                  type="text"
                  placeholder="Pesquisar por nome, WhatsApp ou e-mail..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-1.5 text-xs bg-slate-50 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold">
                <span>{filteredProfiles.length} contato(s) exibido(s)</span>
              </div>
            </div>

            {/* FULL DATA TABLE (GoHighLevel Clean Style) */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                  <tr>
                    <th className="py-3 px-4">Nome & Contato</th>
                    <th className="py-3 px-4">WhatsApp / Telefone</th>
                    <th className="py-3 px-4">Email</th>
                    <th className="py-3 px-4">Tipo</th>
                    <th className="py-3 px-4">LTV Gerado</th>
                    <th className="py-3 px-4">Pendente</th>
                    <th className="py-3 px-4">Origem</th>
                    <th className="py-3 px-4 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredProfiles.length > 0 ? (
                    filteredProfiles.map(p => {
                      const cleanPhone = p.phone.replace(/[^0-9]/g, '');

                      return (
                        <tr 
                          key={p.id}
                          onClick={() => setSelectedProfileId(p.id)}
                          className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                        >
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 font-black text-slate-700 flex items-center justify-center text-xs group-hover:bg-slate-900 group-hover:text-white transition-colors">
                                {p.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-bold text-slate-900 group-hover:text-slate-950 flex items-center gap-1.5">
                                  {p.name}
                                </div>
                                <div className="text-[10px] text-slate-400">
                                  {new Date(p.createdAt).toLocaleDateString('pt-BR')}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="py-3.5 px-4 font-medium text-slate-700">
                            {p.phone}
                          </td>

                          <td className="py-3.5 px-4 text-slate-500">
                            {p.email || '—'}
                          </td>

                          <td className="py-3.5 px-4">
                            <span className={`px-2 py-0.5 rounded-full font-bold uppercase text-[10px] ${
                              p.type === 'cliente' 
                                ? 'bg-emerald-100 text-emerald-800' 
                                : 'bg-amber-100 text-amber-800'
                            }`}>
                              {p.type === 'cliente' ? 'Cliente' : 'Lead'}
                            </span>
                          </td>

                          <td className="py-3.5 px-4 font-bold text-slate-900">
                            R$ {p.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </td>

                          <td className="py-3.5 px-4">
                            <span className={`font-bold ${p.totalPending > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                              R$ {p.totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </td>

                          <td className="py-3.5 px-4 text-slate-500">
                            {p.source || 'Site'}
                          </td>

                          <td className="py-3.5 px-4 text-right" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => handleOpenNewInvoice(p)}
                                title="Emitir Cobrança Pix"
                                className="p-1.5 rounded-md text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                              >
                                <DollarSign size={14} />
                              </button>
                              <a
                                href={`https://wa.me/55${cleanPhone}`}
                                target="_blank"
                                rel="noreferrer"
                                title="Abrir WhatsApp"
                                className="p-1.5 rounded-md text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
                              >
                                <MessageCircle size={14} />
                              </a>
                              <button
                                onClick={() => setSelectedProfileId(p.id)}
                                className="px-2.5 py-1 rounded-md text-[11px] font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors flex items-center gap-1"
                              >
                                Abrir Dossiê <ChevronRight size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-slate-400">
                        Nenhum contato encontrado com os filtros selecionados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        )}

        {/* TAB: TODAS AS FATURAS & COBRANÇAS */}
        {activeTab === 'cobrancas' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                  <tr>
                    <th className="py-3 px-4">Fatura</th>
                    <th className="py-3 px-4">Cliente / Contato</th>
                    <th className="py-3 px-4">Telefone</th>
                    <th className="py-3 px-4">Valor</th>
                    <th className="py-3 px-4">Vencimento</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.length > 0 ? (
                    invoices.map(inv => {
                      const isPaid = inv.status === 'paid';
                      const publicLink = `${window.location.origin}/#/fatura/${inv.id}`;

                      return (
                        <tr key={inv.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-slate-900">{inv.title}</div>
                            <div className="text-[10px] font-mono text-slate-400">{inv.invoiceNumber}</div>
                          </td>
                          <td className="py-3.5 px-4 font-medium text-slate-900">
                            {inv.customerName}
                          </td>
                          <td className="py-3.5 px-4 text-slate-600">
                            {inv.customerPhone}
                          </td>
                          <td className="py-3.5 px-4 font-black text-slate-900">
                            R$ {inv.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3.5 px-4 text-slate-600">
                            {new Date(inv.dueDate).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className={`px-2 py-0.5 rounded-full font-bold uppercase text-[10px] ${
                              isPaid 
                                ? 'bg-emerald-100 text-emerald-800' 
                                : 'bg-amber-100 text-amber-800'
                            }`}>
                              {isPaid ? 'Pago' : 'Pendente'}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {!isPaid && (
                                <button
                                  onClick={() => handleMarkInvoiceAsPaid(inv.id)}
                                  className="px-2 py-1 rounded-md font-bold text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                                >
                                  Marcar Pago
                                </button>
                              )}
                              <a
                                href={publicLink}
                                target="_blank"
                                rel="noreferrer"
                                className="px-2.5 py-1 rounded-md font-bold text-[11px] text-slate-700 bg-slate-100 hover:bg-slate-200"
                              >
                                Ver Fatura
                              </a>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-slate-400">
                        Nenhuma fatura emitida ainda.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB: CONFIGURAÇÕES DE RECEBIMENTO */}
        {activeTab === 'config_pagamento' && (
          <div className="max-w-4xl bg-white p-8 rounded-xl border border-slate-200 shadow-xs space-y-6">
            <div className="pb-4 border-b border-slate-100">
              <h2 className="text-lg font-black text-slate-900">Formas de Recebimento do Estabelecimento</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Defina sua chave Pix oficial e gateways para recebimento sem intermediários.
              </p>
            </div>

            <form onSubmit={handleSavePaymentConfig} className="space-y-6">
              
              {/* PIX */}
              <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase text-slate-900 tracking-wider flex items-center gap-2">
                  <QrCode size={15} className="text-emerald-600" /> 1. Chave Pix Direta (Sem Taxas)
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Tipo de Chave</label>
                    <select
                      value={pixKeyType}
                      onChange={(e: any) => setPixKeyType(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-medium"
                    >
                      <option value="cnpj">CNPJ</option>
                      <option value="cpf">CPF</option>
                      <option value="email">E-mail</option>
                      <option value="phone">Telefone</option>
                      <option value="random">Chave Aleatória</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 mb-1">Chave Pix</label>
                    <input
                      type="text"
                      placeholder="sua-chave@pix.com.br ou 00.000.000/0001-00"
                      value={pixKey}
                      onChange={e => setPixKey(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-medium"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Nome do Titular / Razão Social</label>
                    <input
                      type="text"
                      placeholder={client.data?.name}
                      value={pixReceiverName}
                      onChange={e => setPixReceiverName(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Cidade da Conta</label>
                    <input
                      type="text"
                      placeholder="São Paulo"
                      value={pixCity}
                      onChange={e => setPixCity(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-medium"
                    />
                  </div>
                </div>
              </div>

              {/* CARD LINK */}
              <div className="space-y-3 pt-4 border-t border-slate-100">
                <h3 className="text-xs font-bold uppercase text-slate-900 tracking-wider flex items-center gap-2">
                  <CreditCard size={15} className="text-blue-600" /> 2. Link de Pagamento com Cartão de Crédito
                </h3>
                
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Link de Pagamento (Mercado Pago, InfinitePay, Stone, Stripe, etc.)</label>
                  <input
                    type="url"
                    placeholder="https://mpago.la/xxxx ou link da maquininha"
                    value={cardPaymentLink}
                    onChange={e => setCardPaymentLink(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-medium"
                  />
                </div>
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={savingConfig}
                  className="px-5 py-2.5 rounded-lg text-xs font-bold text-white shadow-sm hover:opacity-90 transition-all"
                  style={{ backgroundColor: primaryColor }}
                >
                  {savingConfig ? 'Salvando...' : 'Salvar Formas de Recebimento'}
                </button>
              </div>
            </form>
          </div>
        )}

      </div>

      {/* MODAL: EMITIR COBRANÇA */}
      {isInvoiceModalOpen && invoiceTargetProfile && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl p-6 border border-slate-200 animate-in fade-in">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <h3 className="font-black text-slate-900 text-base">Emitir Cobrança Instantânea</h3>
                <p className="text-xs text-slate-500">Para: <b>{invoiceTargetProfile.name}</b></p>
              </div>
              <button onClick={() => setIsInvoiceModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateInvoice} className="space-y-4 pt-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Título do Serviço</label>
                <input 
                  type="text" 
                  value={invoiceTitle}
                  onChange={e => setInvoiceTitle(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-bold"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 uppercase mb-1">Valor (R$)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={invoiceAmount}
                    onChange={e => setInvoiceAmount(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-black text-emerald-700 text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 uppercase mb-1">Vencimento</label>
                  <input 
                    type="date" 
                    value={invoiceDueDate}
                    onChange={e => setInvoiceDueDate(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-medium"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Descrição</label>
                <textarea 
                  value={invoiceDescription}
                  onChange={e => setInvoiceDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200"
                />
              </div>

              <div className="pt-3 flex justify-end gap-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsInvoiceModalOpen(false)}
                  className="px-3.5 py-2 rounded-lg border border-slate-200 text-slate-600 font-bold hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingInvoice}
                  className="px-4 py-2 rounded-lg font-bold text-white shadow-sm flex items-center gap-1.5 hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Send size={13} /> {isSubmittingInvoice ? 'Gerando...' : 'Gerar e Enviar no WhatsApp'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: NOVO CONTATO */}
      {isAddCustomerModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 border border-slate-200 animate-in fade-in">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <h3 className="font-black text-slate-900 text-base">Novo Lead / Cliente</h3>
              <button onClick={() => setIsAddCustomerModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateCustomer} className="space-y-3 pt-3 text-xs">
              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Nome Completo</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Dra. Mariana Costa"
                  value={newCustomerName}
                  onChange={e => setNewCustomerName(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 uppercase mb-1">WhatsApp / Celular</label>
                  <input
                    type="tel"
                    required
                    placeholder="(11) 99999-8888"
                    value={newCustomerPhone}
                    onChange={e => setNewCustomerPhone(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-medium"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 uppercase mb-1">Tipo de Registro</label>
                  <select
                    value={newCustomerType}
                    onChange={(e: any) => setNewCustomerType(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-medium"
                  >
                    <option value="lead">Lead (Em Aberto)</option>
                    <option value="cliente">Cliente Ativo</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">E-mail (Opcional)</label>
                <input
                  type="email"
                  placeholder="contato@cliente.com"
                  value={newCustomerEmail}
                  onChange={e => setNewCustomerEmail(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 rounded-lg border border-slate-200 font-medium"
                />
              </div>

              <div className="pt-3 flex justify-end gap-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAddCustomerModalOpen(false)}
                  className="px-3.5 py-2 rounded-lg border border-slate-200 text-slate-600 font-bold hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg font-bold text-white shadow-sm hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                >
                  Salvar Contato
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
